import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { getLeadType, handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useDeleteEnquiry, useGetEnquiries } from "../api-clients";
import useEnquiryTableColumns from "./useEnquiryTableColumns";

const EnquiryList = () => {
  useSetActiveTab(TAB_NAMES.ENQUIRY_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updateEnquiryData,
    isValidating,
  } = useGetEnquiries({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(updateEnquiryData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteEnquiry,
  } = useDeleteEnquiry();

  const enquiriesData = useMemo(() => {
    const mappedData = data?.data?.rows?.map((e: any) => ({
      ...e,
      leadType: getLeadType(e.leadType),
    }));
    return mappedData ?? [];
  }, [data?.data?.rows]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useEnquiryTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateEnquiryData();
      setIsDeleteModalOpen(false);
      successNotification("Enquiry Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateEnquiryData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteEnquiry(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Enquiry Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(enquiriesData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Enquiry",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={enquiriesData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default EnquiryList;
